import { Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { HelpLabel } from '@/features/guided-help';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CATEGORY_RANKING_TARGET,
  type CategoryRankingTarget,
  type Category,
} from '@/types/tournament.model';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export interface CountsForCategoryRankingFieldProps {
  value: boolean;
  onChange: (value: boolean) => void;
  /** Eligible categories: those within the tournament's range. Empty disables the fixed mode. */
  categories: Category[];
  target: CategoryRankingTarget;
  onTargetChange: (target: CategoryRankingTarget) => void;
  categoryId: number | null;
  onCategoryChange: (categoryId: number) => void;
  wrapInCard?: boolean;
  inputId?: string;
}

export function CountsForCategoryRankingField({
  value,
  onChange,
  categories,
  target,
  onTargetChange,
  categoryId,
  onCategoryChange,
  wrapInCard = true,
  inputId = 'counts-for-category-ranking',
}: CountsForCategoryRankingFieldProps) {
  const field = (
    <div className="space-y-3">
      <div
        className="flex items-start justify-between gap-4"
        data-guide-id="wizard.config.countsForCategoryRanking"
      >
        <div className="space-y-1">
          <HelpLabel
            htmlFor={inputId}
            topicId="wizard.config.countsForCategoryRanking"
          >
            Suma al ranking de categoría
          </HelpLabel>
          <p className="text-sm text-gray-500">
            Si está activo, los jugadores ganan puntos para el ranking de su
            categoría según la instancia que alcancen en este torneo. No afecta
            al ELO, que siempre se calcula.
          </p>
        </div>
        <Switch id={inputId} checked={value} onCheckedChange={onChange} />
      </div>

      {value && (
        <div
          className="space-y-3 border-t border-border pt-3"
          data-guide-id="wizard.config.categoryRankingTarget"
        >
          <HelpLabel
            htmlFor={`${inputId}-target`}
            topicId="wizard.config.categoryRankingTarget"
          >
            ¿A qué ranking suman los puntos?
          </HelpLabel>
          <Select
            value={target}
            onValueChange={(next) => onTargetChange(next as CategoryRankingTarget)}
          >
            <SelectTrigger id={`${inputId}-target`} className="w-full sm:w-[320px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CATEGORY_RANKING_TARGET.PLAYER_CATEGORY}>
                A la categoría de cada jugador
              </SelectItem>
              <SelectItem value={CATEGORY_RANKING_TARGET.FIXED_CATEGORY}>
                Todo a una categoría fija
              </SelectItem>
            </SelectContent>
          </Select>

          {target === CATEGORY_RANKING_TARGET.FIXED_CATEGORY && (
            <div className="space-y-2">
              <Label htmlFor={`${inputId}-category`}>Categoría</Label>
              <Select
                value={categoryId !== null ? String(categoryId) : ''}
                onValueChange={(next) => onCategoryChange(Number(next))}
                disabled={categories.length <= 1}
              >
                <SelectTrigger id={`${inputId}-category`} className="w-full sm:w-[220px]">
                  <SelectValue placeholder="Elegí una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Without a target category the backend rejects the tournament, so say
                  why instead of leaving the step blocked with no explanation. */}
              {categories.length === 0 ? (
                <p className="text-sm text-red-600">
                  No hay categorías disponibles para el rango del torneo. Volvé a
                  &ldquo;A la categoría de cada jugador&rdquo; o revisá las
                  categorías del torneo.
                </p>
              ) : (
                categoryId === null && (
                  <p className="text-sm text-red-600">
                    Elegí la categoría destino para poder continuar.
                  </p>
                )
              )}
              <p className="text-sm text-gray-500">
                Todos los puntos de este torneo van al ranking de esta categoría,
                sin importar la categoría de cada jugador.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!wrapInCard) {
    return <div className={wizardBlockShellClassName}>{field}</div>;
  }

  return (
    <Card className={wizardBlockCardClassName}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-blue-600" />
          Ranking de categoría
        </CardTitle>
      </CardHeader>
      <CardContent>{field}</CardContent>
    </Card>
  );
}
